import { useCallback, useMemo, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import {
  joinReadinessForInvite,
  type InviteAuthHint,
} from "../data/invite-join-readiness";
import type { MemberInviteRow } from "../data/member-invites";
import type { MemberRow } from "../data/members";
import {
  isPlatformPeopleSearchCapped,
  normalizeInviteEmailQuery,
  normalizePlatformPeopleSearchQuery,
  redactPlatformPeopleEmail,
  searchPlatformPeople,
  type PlatformInviteSearchHit,
  type PlatformPersonSearchHit,
} from "../data/platform-people-search";
import { formatDisplayTime } from "../lib/display-time";
import { PlatformBulkInvite } from "./PlatformBulkInvite";
import { PlatformMemberDirectory } from "./MemberDirectory";
import {
  PlatformMemberInvitePanel,
  type PlatformMemberInviteRequest,
} from "./MemberInvitePanel";

// Platform Accounts P2: the selected workspace gets the same invite and lifecycle surfaces as
// Admin -> Members. There is no founder-only membership wire. The wrappers only adjust the
// selected-tenant presentation; every mutation still uses the shipped member.manage RPCs.
export function PlatformMembersDesk({
  client,
  accountId,
  timeZone,
  pollMs,
  canManage,
  canSupportAs,
  onSupportStarted,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  canManage: boolean;
  canSupportAs: boolean;
  onSupportStarted: () => void;
}) {
  const [query, setQuery] = useState("");
  const [memberRows, setMemberRows] = useState<readonly MemberRow[] | null>(null);
  const [inviteRows, setInviteRows] = useState<readonly MemberInviteRow[] | null>(null);
  const [inviteAuthHints, setInviteAuthHints] = useState<ReadonlyMap<string, InviteAuthHint>>(
    () => new Map(),
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [revealedHitKey, setRevealedHitKey] = useState<string | null>(null);
  const [inviteRequest, setInviteRequest] = useState<PlatformMemberInviteRequest | null>(null);
  const [inviteReloadSignal, setInviteReloadSignal] = useState(0);
  const requestId = useRef(0);

  const normalizedQuery = normalizePlatformPeopleSearchQuery(query);
  const searchReady = memberRows !== null && inviteRows !== null;
  const matches = useMemo(
    () =>
      searchReady
        ? searchPlatformPeople(memberRows, inviteRows, normalizedQuery)
        : [],
    [inviteRows, memberRows, normalizedQuery, searchReady],
  );
  const selected = matches.find((hit) => hit.key === selectedKey) ?? matches[0] ?? null;
  const searchIsCapped = isPlatformPeopleSearchCapped(memberRows ?? [], inviteRows ?? []);

  const captureMemberRows = useCallback((rows: readonly MemberRow[] | null) => {
    setMemberRows(rows);
  }, []);
  const captureInviteRows = useCallback(
    (
      rows: readonly MemberInviteRow[] | null,
      authHints: ReadonlyMap<string, InviteAuthHint>,
    ) => {
      setInviteRows(rows);
      setInviteAuthHints(authHints);
    },
    [],
  );

  function changeQuery(value: string): void {
    setQuery(value);
    setSelectedKey(null);
    setRevealedHitKey(null);
  }

  function clearSearch(): void {
    changeQuery("");
  }

  function dispatchInviteRequest(
    request:
      | Omit<Extract<PlatformMemberInviteRequest, { kind: "prefill-email" }>, "requestId">
      | Omit<Extract<PlatformMemberInviteRequest, { kind: "inspect-invite" }>, "requestId">
      | Omit<Extract<PlatformMemberInviteRequest, { kind: "resend-invite" }>, "requestId">
      | Omit<Extract<PlatformMemberInviteRequest, { kind: "revoke-invite" }>, "requestId">,
  ): void {
    requestId.current += 1;
    setInviteRequest({ ...request, requestId: requestId.current });
  }

  return (
    <section className="cockpit-section cockpit-members admin-members" aria-label="Members and owners">
      <div className="cockpit-members__head">
        <div>
          <h3 className="cockpit-section__title">Members and owners</h3>
          <p className="cockpit-members__note">
            Invite and manage this workspace without switching to Admin. Owner is a reading of an
            admin membership, not a separate role.
          </p>
        </div>
        <div className="platform-people-search__head-tools">
          <div className="platform-people-search__field">
            <label htmlFor="platform-people-search-input">Search members and invites</label>
            <span className="platform-people-search__input-wrap">
              <input
                id="platform-people-search-input"
                type="search"
                value={query}
                onChange={(event) => changeQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") clearSearch();
                }}
                placeholder="Name or email"
                autoComplete="off"
                spellCheck={false}
              />
              {normalizedQuery.length > 0 ? (
                <button type="button" onClick={clearSearch}>Clear</button>
              ) : null}
            </span>
          </div>
          <span className="cockpit-members__scope">Selected workspace</span>
          {canManage ? (
            <PlatformBulkInvite
              client={client}
              accountId={accountId}
              canManage={canManage}
              memberRows={memberRows}
              inviteRows={inviteRows}
              onCompleted={() => setInviteReloadSignal((value) => value + 1)}
            />
          ) : null}
          {searchIsCapped ? (
            <p className="platform-people-search__cap">Search covers the loaded window only.</p>
          ) : null}
        </div>
      </div>
      {normalizedQuery.length > 0 ? (
        <section className="platform-people-search" aria-label="People search">
          {!searchReady ? (
            <p className="surface__state" role="status">
              People search is unavailable until members and invites are ready.
            </p>
          ) : matches.length === 0 ? (
            <EmptyPersonCard
              canInvite={canManage}
              inviteEmail={normalizeInviteEmailQuery(query)}
              onSendInvite={(email) => dispatchInviteRequest({ kind: "prefill-email", email })}
            />
          ) : (
            <div className="platform-people-search__layout">
              <div className="platform-people-search__matches">
                <p className="platform-people-search__matches-title">
                  Matches <span>{matches.length}</span>
                </p>
                <ul>
                  {matches.map((hit) => (
                    <li key={hit.key}>
                      <button
                        type="button"
                        aria-pressed={selected?.key === hit.key}
                        onClick={() => {
                          setSelectedKey(hit.key);
                          setRevealedHitKey(null);
                        }}
                      >
                        <span className="platform-people-search__match-name">
                          <strong>{hit.displayName}</strong>
                          <span>{hit.kind === "member" ? humanizeLabel(hit.status) : "Pending"}</span>
                        </span>
                        <span>
                          {humanizeLabel(hit.role)} · {hit.kind === "member" ? "member" : "invite"} ·{" "}
                          {redactPlatformPeopleEmail(hit.email)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              {selected !== null ? (
                <SelectedPersonCard
                  hit={selected}
                  timeZone={timeZone}
                  canManage={canManage}
                  authHint={
                    selected.kind === "invite"
                      ? (inviteAuthHints.get(selected.inviteId) ?? null)
                      : null
                  }
                  contactRevealed={revealedHitKey === selected.key}
                  onToggleContact={() =>
                    setRevealedHitKey((current) => current === selected.key ? null : selected.key)
                  }
                  onInspectInvite={(invite) =>
                    dispatchInviteRequest({ kind: "inspect-invite", inviteId: invite.inviteId })
                  }
                  onResendInvite={(invite) =>
                    dispatchInviteRequest({ kind: "resend-invite", inviteId: invite.inviteId })
                  }
                  onRevokeInvite={(invite) =>
                    dispatchInviteRequest({ kind: "revoke-invite", inviteId: invite.inviteId })
                  }
                />
              ) : null}
            </div>
          )}
        </section>
      ) : null}
      <PlatformMemberInvitePanel
        client={client}
        accountId={accountId}
        timeZone={timeZone}
        canManage={canManage}
        onRowsSnapshot={captureInviteRows}
        request={inviteRequest}
        reloadSignal={inviteReloadSignal}
      />
      <PlatformMemberDirectory
        client={client}
        accountId={accountId}
        timeZone={timeZone}
        canManage={canManage}
        canSupportAs={canSupportAs}
        onSupportStarted={onSupportStarted}
        onRowsSnapshot={captureMemberRows}
        {...(pollMs !== undefined ? { pollMs } : {})}
      />
    </section>
  );
}

function SelectedPersonCard({
  hit,
  timeZone,
  canManage,
  authHint,
  contactRevealed,
  onToggleContact,
  onInspectInvite,
  onResendInvite,
  onRevokeInvite,
}: {
  hit: PlatformPersonSearchHit;
  timeZone: string;
  canManage: boolean;
  authHint: InviteAuthHint;
  contactRevealed: boolean;
  onToggleContact: () => void;
  onInspectInvite: (invite: PlatformInviteSearchHit) => void;
  onResendInvite: (invite: PlatformInviteSearchHit) => void;
  onRevokeInvite: (invite: PlatformInviteSearchHit) => void;
}) {
  const contact = contactRevealed
    ? (hit.email ?? "Not provided")
    : redactPlatformPeopleEmail(hit.email);

  return (
    <article className="platform-person-card" aria-label="Selected person">
      <header>
        <div>
          <h4>{hit.displayName}</h4>
          <p>{humanizeLabel(hit.role)}</p>
        </div>
        <span className={`platform-person-card__truth platform-person-card__truth--${hit.kind}`}>
          {hit.kind === "member"
            ? `${humanizeLabel(hit.status)} membership`
            : "Open invite"}
        </span>
      </header>
      <dl>
        <div>
          <dt>Contact</dt>
          <dd>
            <span>{contact}</span>
            {hit.email !== null ? (
              <button type="button" aria-pressed={contactRevealed} onClick={onToggleContact}>
                {contactRevealed ? "Hide" : "Reveal"}
              </button>
            ) : null}
          </dd>
        </div>
        {hit.kind === "member" ? (
          <>
            <div>
              <dt>Membership</dt>
              <dd>{humanizeLabel(hit.status)} · {humanizeLabel(hit.role)}</dd>
            </div>
            <div>
              <dt>Added</dt>
              <dd>{formatPersonTime(hit.createdAt, timeZone)}</dd>
            </div>
          </>
        ) : (
          <>
            <div>
              <dt>Open invite</dt>
              <dd>Pending · {humanizeLabel(hit.role)}</dd>
            </div>
            <div>
              <dt>Expires</dt>
              <dd>{formatPersonTime(hit.expiresAt, timeZone)}</dd>
            </div>
            <div>
              <dt>Join readiness</dt>
              <dd>{joinReadinessForInvite({ status: hit.status, authHint }).label}</dd>
            </div>
          </>
        )}
      </dl>
      {hit.kind === "invite" ? (
        <footer className="platform-person-card__actions">
          {canManage ? (
            <>
              <button
                type="button"
                className="builder__navbtn"
                onClick={() => onResendInvite(hit)}
              >
                Resend sign-in email
              </button>
              <button
                type="button"
                className="builder__cancel"
                onClick={() => onRevokeInvite(hit)}
              >
                Revoke
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="builder__navbtn"
            onClick={() => onInspectInvite(hit)}
          >
            Why can&apos;t they get in?
          </button>
        </footer>
      ) : null}
    </article>
  );
}

function EmptyPersonCard({
  canInvite,
  inviteEmail,
  onSendInvite,
}: {
  canInvite: boolean;
  inviteEmail: string | null;
  onSendInvite: (email: string) => void;
}) {
  return (
    <article className="platform-person-card platform-person-card--empty" aria-label="Selected person">
      <h4>No membership and no open invite.</h4>
      <p>Search another name or email, or send an invite when the address is known.</p>
      {canInvite && inviteEmail !== null ? (
        <button
          type="button"
          className="minter__confirm-go"
          onClick={() => onSendInvite(inviteEmail)}
        >
          Send invite
        </button>
      ) : null}
    </article>
  );
}

function formatPersonTime(value: string | null, timeZone: string): string {
  return value === null || Number.isNaN(Date.parse(value))
    ? "Not available"
    : formatDisplayTime(value, timeZone);
}
